import React, { useState, useEffect } from 'react';
import { Sparkles, Check } from '../../icons';
import { useAIPolicy } from '../../hooks/useAIPolicy';
import Button from '../ui/Button';
import Toggle from '../ui/Toggle';
import { CardSection } from '../ui/Card';

const PROVIDERS = [
  { id: 'gemini', label: 'Google Gemini', models: ['gemini-3.6-flash', 'gemini-3-flash-preview', 'gemini-2.5-flash', 'gemini-2.5-pro'] },
  { id: 'claude', label: 'Anthropic Claude', models: ['claude-opus-4-8', 'claude-sonnet-4-6', 'claude-haiku-4-5'] },
  { id: 'openai', label: 'OpenAI', models: ['gpt-5.5', 'gpt-5.4', 'gpt-5.4-mini'] },
  { id: 'perplexity', label: 'Perplexity', models: ['sonar-pro', 'sonar', 'sonar-reasoning'] },
];

const AIPolicySettings = ({ userId }) => {
  const { policy, isLoading, updatePolicy } = useAIPolicy();
  const [form, setForm] = useState(policy);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setForm(policy);
    setDirty(false);
  }, [policy]);

  const change = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setDirty(true);
    setMessage(null);
  };

  const models = PROVIDERS.find((p) => p.id === form.defaultProvider)?.models || [];

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await updatePolicy(form, userId);
      setDirty(false);
      setMessage({ type: 'success', text: 'AI policy saved.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-card border border-line rounded-card p-4 animate-pulse" aria-busy="true">
        <div className="h-5 bg-card-2 rounded w-1/3" />
      </div>
    );
  }

  return (
    <CardSection
      title={
        <span className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-accent" /> AI policy (org defaults)
        </span>
      }
      className="space-y-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label" htmlFor="policy-provider">Default provider</label>
          <select
            id="policy-provider"
            value={form.defaultProvider}
            onChange={(e) => {
              const p = e.target.value;
              const firstModel = PROVIDERS.find((x) => x.id === p)?.models?.[0];
              change({ defaultProvider: p, defaultModel: firstModel });
            }}
            className="input"
          >
            {PROVIDERS.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="label" htmlFor="policy-model">Default model</label>
          <select id="policy-model" value={form.defaultModel} onChange={(e) => change({ defaultModel: e.target.value })} className="input">
            {models.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-primary">Allow users to select their own model</span>
        <Toggle
          checked={!!form.allowUserModelSelection}
          onChange={(v) => change({ allowUserModelSelection: v })}
          label="Allow users to select their own model"
          size="sm"
        />
      </div>

      {message && (
        <div
          role="status"
          className={`p-3 rounded-xl text-sm border ${
            message.type === 'success' ? 'bg-accent-soft text-accent border-accent/30' : 'bg-danger-soft text-danger border-danger/30'
          }`}
        >
          {message.text}
        </div>
      )}

      <Button variant="primary" onClick={save} disabled={!dirty || saving} loading={saving}>
        {!saving && <Check className="w-4 h-4" />}
        {saving ? 'Saving…' : 'Save policy'}
      </Button>
    </CardSection>
  );
};

export default AIPolicySettings;
